import { AccommodationsSearchRequestDto } from '@/features/AccommodationList/api/requestTypes';
import { TravelerType } from '@/types/widgetConfig';
import dayjs from 'dayjs';
import { useAtom } from 'jotai';
import { useMemo } from 'react';
import { z } from 'zod';

import { customerInfoAtom } from './customerInfo';

const initialRequestSchema = z.object({
  checkIn: z.string().nonempty(),
  checkOut: z.string().nonempty(),
  adults: z.number().nonnegative(),
  children: z.number().nonnegative(),
  rooms: z.number().nonnegative(),
  radiusInMeters: z.number().nonnegative(),
  location: z.object({
    city: z.string().nonempty()
  })
});
const useCreateInitialRequest = () => {
  const [customerInfoState] = useAtom(customerInfoAtom);

  const initialRequest = useMemo(() => {
    if (customerInfoState.isInitialized) {
      const accommodationSearchRequest: AccommodationsSearchRequestDto = {
        radiusInMeters: 0,
        checkIn: '',
        checkOut: '',
        adults: 1,
        children: 0,
        rooms: 1
      };
      const eventDate = dayjs(customerInfoState.customerInfo?.eventDate);
      if (eventDate.isValid() && eventDate.isAfter(dayjs())) {
        accommodationSearchRequest.checkIn = eventDate
          .startOf('day')
          .add(-1, 'day')
          .format('YYYY-MM-DD');

        accommodationSearchRequest.checkOut = eventDate
          .endOf('day')
          .add(1, 'day')
          .format('YYYY-MM-DD');
      }
      if (customerInfoState.customerInfo?.destination) {
        accommodationSearchRequest.location = {
          city: customerInfoState.customerInfo.destination
        };
      }

      let travelers = ['adults']; // default fallback
      let adults = 0,
        children = 0,
        rooms = 0;

      if (customerInfoState.customerInfo?.travelers) {
        const travelersData = customerInfoState.customerInfo.travelers;

        if (Array.isArray(travelersData) && travelersData.length > 0) {
          // Handle array case: ['adults', 'adults', 'child']
          travelers = travelersData;
          travelers.forEach(t => {
            if (t === TravelerType.Adult) {
              adults++;
            } else {
              children++;
            }
          });
          rooms = travelers.length;
        } else if (
          typeof travelersData === 'number' ||
          !Number.isNaN(Number(travelersData))
        ) {
          const travelerNumber = Number(travelersData);
          // Handle number case: assume all are adults and no rooms defined
          adults = travelerNumber;
        }
      }

      accommodationSearchRequest.adults = adults;
      accommodationSearchRequest.children = children;
      accommodationSearchRequest.rooms = rooms;
      accommodationSearchRequest.freeCancellataion = false;
      accommodationSearchRequest.radiusInMeters = 5000;

      try {
        initialRequestSchema.parse(accommodationSearchRequest);
        return accommodationSearchRequest;
      } catch {
        return undefined;
      }
    }
  }, [customerInfoState.isInitialized]);

  return { initialRequest };
};

export default useCreateInitialRequest;
